<script setup>
// SiteUp: Novidades. Uma secao por versao publicada, da mais nova para a mais
// antiga, com um item por mudanca que o cliente ve. O conteudo vem do arquivo
// versionado helper/releaseNotes.js, nunca do banco: a nota tem que descrever
// exatamente a imagem que a pessoa esta rodando.
// A TELA nao tem gate de superficie: nota de versao se le no celular tambem.
// O botao "Ler o manual" tem: manual, dica, tour e central de ajuda so existem
// no desktop web, entao no celular e no PWA o atalho nao pode nem aparecer.
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocale } from 'shared/composables/useLocale';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useReleaseNotes } from 'dashboard/composables/useReleaseNotes';
import { useOnboardingSurface } from 'dashboard/composables/useOnboardingSurface';
import { openPageManual } from 'dashboard/composables/usePageCoach';
import {
  DEFAULT_SCREEN_ICON,
  PAGE_COACH,
  screenIconFor,
} from 'dashboard/helper/onboardingRegistry';
import { HUB_WORLDS } from 'dashboard/routes/dashboard/hub/hubWorlds';
import BrasaMascot from 'dashboard/components-next/onboarding/BrasaMascot.vue';

const { t } = useI18n();
// O locale do app vem com underscore (pt_BR) e o Intl estoura RangeError com
// esse formato. resolvedLocale normaliza para BCP-47 e cai para en se preciso.
const { resolvedLocale } = useLocale();
// Nem toda tela citada numa nota e acessivel para quem le: relatorio pede
// permissao, Integracoes e de administrador. Sem esta checagem o botao
// levaria a pessoa para um redirect seco.
const { shouldShow } = usePolicy();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { releases, seenBuild, isUnread, markAllRead } = useReleaseNotes();
// Abrir o manual daqui abriria no celular uma superficie que nao existe ali.
// A tela fica, o atalho para o manual so no desktop web.
const { enabled: surfaceEnabled } = useOnboardingSurface();

const worldIcon = id =>
  HUB_WORLDS.find(world => world.id === id)?.icon || DEFAULT_SCREEN_ICON;

const iconFor = entry =>
  entry.routeName ? screenIconFor(entry.routeName) : worldIcon(entry.world);

// A data vem como AAAA-MM-DD. `new Date('2026-08-23')` seria meia-noite UTC e
// viraria dia 22 no fuso do Brasil, entao a hora entra explicita.
const formatDate = value => {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(resolvedLocale.value, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date);
};

// Tela que precisa de parametro (uma conversa aberta, por exemplo) nao tem
// "ir para a tela" generico: sobra o manual no desktop web, e no celular a
// linha fica sem acao nenhuma. Mesma guarda da central de ajuda.
const routeFor = entry => {
  if (!entry.routeName) return null;
  const target = router.getRoutes().find(item => item.name === entry.routeName);
  if (!target) return null;
  if (/:(?!accountId)/.test(target.path)) return null;
  const meta = target.meta || {};
  if (
    !shouldShow(
      meta.featureFlag || '',
      meta.permissions ?? [],
      meta.installationTypes ?? []
    )
  ) {
    return null;
  }
  return accountScopedRoute(
    entry.routeName,
    {},
    entry.view ? { view: entry.view } : {}
  );
};

const newestBuild = computed(() => releases.value[0]?.build ?? null);

// Quem nunca abriu a tela veria o selo nas oito versoes, o que marca tudo e
// nao informa nada: sem leitura anterior o selo fica so na mais nova. Os dois
// ramos leem o snapshot `seenBuild` do composable, entao o selo nao some na
// frente de quem esta lendo (a tela marca tudo como visto ao abrir).
const isNewSection = build =>
  seenBuild.value == null ? build === newestBuild.value : isUnread(build);

const sections = computed(() =>
  releases.value.map(entry => ({
    build: entry.build,
    version: entry.version,
    date: formatDate(entry.date),
    isNew: isNewSection(entry.build),
    items: entry.items.map(item => {
      const to = routeFor(item);
      const manualKey =
        surfaceEnabled.value && PAGE_COACH[item.coachKey]
          ? item.coachKey
          : null;
      return {
        testId: `${entry.build}-${item.id}`,
        icon: iconFor(item),
        kindKey: `RELEASE_NOTES.KIND.${item.kind.toUpperCase()}`,
        isHighlight: item.kind === 'new',
        titleKey: item.titleKey,
        bodyKey: item.bodyKey,
        manualKey,
        to,
        // Item de rota com parametro nao tem "ir para a tela"; no celular
        // tambem perde o manual. Sem nenhum dos dois a barra de acoes nao
        // renderiza, senao sobra um container vazio de largura zero no DOM.
        hasActions: Boolean(to || manualKey),
      };
    }),
  }))
);

// A tela renderizava todas as versoes (mais de 80) numa pagina so: o que
// mudou agora ficava perdido numa rolagem longa, e o DOM pesava no celular.
// Ficam as nao lidas mais as ultimas RECENT_COUNT; o resto abre num botao.
const RECENT_COUNT = 5;
const showOlder = ref(false);
const visibleSections = computed(() =>
  showOlder.value
    ? sections.value
    : sections.value.filter(
        (section, index) => index < RECENT_COUNT || section.isNew
      )
);
const hiddenCount = computed(
  () => sections.value.length - visibleSections.value.length
);

// O botao sai do DOM no clique. Sem mover o foco, teclado e leitor de tela
// voltam para o body e nao sabem que as versoes entraram na lista: o foco vai
// para a primeira versao que estava escondida.
const pageRoot = ref(null);
const showOlderVersions = async () => {
  const shown = new Set(visibleSections.value.map(section => section.build));
  const firstHidden = sections.value.find(
    section => !shown.has(section.build)
  )?.build;
  showOlder.value = true;
  if (firstHidden == null) return;
  await nextTick();
  pageRoot.value
    ?.querySelector(`[data-testid="release-notes-version-${firstHidden}"]`)
    ?.focus();
};

// Vale o snapshot, nao o estado ao vivo: a tela marca tudo como visto ao
// abrir, e "voce esta em dia" nao pode aparecer no mesmo instante para quem
// acabou de chegar com novidade.
const somethingNew = computed(() => sections.value.some(item => item.isNew));

const readManual = key => openPageManual(key);

onMounted(() => {
  // Depois do snapshot do composable: o selo Novo continua na tela durante
  // esta visita e some na proxima.
  markAllRead();
});
</script>

<template>
  <div
    ref="pageRoot"
    class="flex h-full w-full flex-col overflow-y-auto bg-n-background"
    data-testid="page-release-notes"
  >
    <div class="mx-auto flex w-full max-w-4xl flex-col gap-6 px-6 py-8">
      <header class="flex items-start gap-4">
        <BrasaMascot :size="56" state="idle" :look="90" class="shrink-0" />
        <div class="flex min-w-0 flex-col gap-1">
          <h1 class="m-0 text-2xl font-semibold text-n-slate-12">
            {{ t('RELEASE_NOTES.TITLE') }}
          </h1>
          <p class="m-0 max-w-2xl text-sm leading-relaxed text-n-slate-11">
            {{ t('RELEASE_NOTES.BODY') }}
          </p>
        </div>
      </header>

      <p
        v-if="!sections.length"
        class="m-0 rounded-xl border border-n-weak bg-n-solid-2 p-4 text-sm text-n-slate-11"
        data-testid="release-notes-empty"
      >
        {{ t('RELEASE_NOTES.EMPTY') }}
      </p>

      <p
        v-else-if="!somethingNew"
        class="m-0 text-xs text-n-slate-11"
        data-testid="release-notes-caught-up"
      >
        {{ t('RELEASE_NOTES.ALL_CAUGHT_UP') }}
      </p>

      <section
        v-for="section in visibleSections"
        :key="section.build"
        tabindex="-1"
        class="flex flex-col gap-2 rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-n-blue-9"
        :aria-labelledby="`release-notes-heading-${section.build}`"
        :data-testid="`release-notes-version-${section.build}`"
      >
        <h2
          :id="`release-notes-heading-${section.build}`"
          class="m-0 flex flex-wrap items-baseline gap-2"
        >
          <span
            class="text-xs font-semibold uppercase tracking-[0.14em] text-n-slate-11"
          >
            {{ section.version }}
          </span>
          <span class="text-xs text-n-slate-11">{{ section.date }}</span>
          <span
            v-if="section.isNew"
            class="inline-flex min-h-5 items-center rounded-full bg-n-iris-9/15 px-2 text-[11px] font-semibold leading-none text-n-iris-11"
            :data-testid="`release-notes-new-${section.build}`"
          >
            {{ t('RELEASE_NOTES.NEW_PILL') }}
          </span>
        </h2>
        <ul class="m-0 flex list-none flex-col gap-2 p-0">
          <li
            v-for="item in section.items"
            :key="item.testId"
            class="flex flex-wrap items-center gap-3 rounded-2xl border border-n-weak bg-n-solid-2 px-4 py-3"
            :data-testid="`release-notes-item-${item.testId}`"
          >
            <span
              class="flex size-9 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-n-slate-11"
              aria-hidden="true"
            >
              <span class="size-4" :class="item.icon" />
            </span>
            <div class="flex min-w-0 flex-1 flex-col gap-1">
              <span class="flex flex-wrap items-center gap-2">
                <span class="text-sm font-semibold text-n-slate-12">
                  <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
                  {{ t(item.titleKey) }}
                </span>
                <span
                  class="inline-flex min-h-5 items-center rounded-full px-2 text-[11px] font-semibold leading-none"
                  :class="
                    item.isHighlight
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-alpha-2 text-n-slate-11'
                  "
                  :data-testid="`release-notes-kind-${item.testId}`"
                >
                  <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
                  {{ t(item.kindKey) }}
                </span>
              </span>
              <span class="text-xs leading-relaxed text-n-slate-11">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
                {{ t(item.bodyKey) }}
              </span>
            </div>
            <div
              v-if="item.hasActions"
              class="flex shrink-0 items-center gap-2"
              :data-testid="`release-notes-actions-${item.testId}`"
            >
              <router-link
                v-if="item.to"
                :to="item.to"
                class="inline-flex min-h-11 items-center gap-1.5 rounded-full px-3 text-xs font-semibold text-n-slate-12 hover:bg-n-alpha-2 md:min-h-9"
                :data-testid="`release-notes-open-${item.testId}`"
              >
                <span
                  class="i-ri-arrow-right-line size-3.5"
                  aria-hidden="true"
                />
                {{ t('RELEASE_NOTES.OPEN_SCREEN') }}
              </router-link>
              <button
                v-if="item.manualKey"
                type="button"
                class="inline-flex min-h-11 items-center gap-1.5 rounded-full px-3 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 md:min-h-9"
                :data-testid="`release-notes-manual-${item.testId}`"
                @click="readManual(item.manualKey)"
              >
                <span class="i-ri-book-open-line size-3.5" aria-hidden="true" />
                {{ t('RELEASE_NOTES.READ_MANUAL') }}
              </button>
            </div>
          </li>
        </ul>
      </section>

      <button
        v-if="hiddenCount"
        type="button"
        class="inline-flex min-h-11 items-center justify-center gap-1.5 self-center rounded-full border border-n-weak px-4 text-sm font-semibold text-n-slate-12 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
        data-testid="release-notes-show-older"
        @click="showOlderVersions"
      >
        <span class="i-ri-arrow-down-s-line size-4" aria-hidden="true" />
        {{ t('RELEASE_NOTES.SHOW_OLDER', { count: hiddenCount }) }}
      </button>
    </div>
  </div>
</template>
